import { create } from 'zustand'
import { api } from '@/api/client'
import { showToast } from '@/components/ui/Toast'
import type { Activity, Tag } from '@/types/models'

interface VaultState {
  isVerified: boolean
  pinConfigured: boolean
  vaultToken: string | null
  monthActs: Record<string, Activity[]>
  tags: Tag[]
  curMonth: Date

  checkStatus: () => Promise<boolean>
  verifyPin: (pin: string) => Promise<boolean>
  setupPin: (pin: string, password: string) => Promise<string | null>
  changePin: (oldPin: string, newPin: string) => Promise<string | null>
  resetPin: (password: string, newPin: string) => Promise<string | null>
  loadMonth: () => Promise<void>
  loadTags: () => Promise<void>
  navigateMonth: (delta: number) => void
  addActivity: (data: { date: string; tag: string; note?: string; duration?: number; time?: string }) => Promise<boolean>
  updateActivity: (id: string, data: { date: string; tag: string; note?: string; duration?: number; time?: string }) => Promise<boolean>
  deleteActivity: (id: string) => Promise<void>
  addTag: (name: string, icon: string, color: string) => Promise<boolean>
  logout: () => void
}

export const useVaultStore = create<VaultState>()((set, get) => ({
  isVerified: false,
  pinConfigured: false,
  vaultToken: null,
  monthActs: {},
  tags: [],
  curMonth: new Date(),

  checkStatus: async () => {
    try {
      const res = await api.get<{ configured: boolean }>('/api/vault/status')
      set({ pinConfigured: res.configured })
      return res.configured
    } catch {
      return false
    }
  },

  verifyPin: async (pin) => {
    try {
      const res = await api.post<{ ok: boolean; vaultToken: string }>('/api/vault/verify', { pin })
      if (res.ok && res.vaultToken) {
        api.vaultToken = res.vaultToken
        set({ isVerified: true, vaultToken: res.vaultToken })
        return true
      }
      return false
    } catch {
      return false
    }
  },

  setupPin: async (pin, password) => {
    try {
      const res = await api.post<{ ok: boolean; error?: string }>('/api/vault/setup', { pin, password })
      if (res.ok) {
        set({ pinConfigured: true })
        return null
      }
      return res.error || '设置失败'
    } catch {
      return '设置失败'
    }
  },

  changePin: async (oldPin, newPin) => {
    try {
      const res = await api.post<{ ok: boolean; error?: string }>('/api/vault/change-pin', { oldPin, newPin })
      if (res.ok) {
        api.vaultToken = null
        set({ isVerified: false, vaultToken: null })
        return null
      }
      return res.error || '修改失败'
    } catch {
      return '修改失败'
    }
  },

  resetPin: async (password, newPin) => {
    try {
      const res = await api.post<{ ok: boolean; error?: string }>('/api/vault/reset-pin', { password, newPin })
      if (res.ok) {
        api.vaultToken = null
        set({ isVerified: false, vaultToken: null })
        return null
      }
      return res.error || '重置失败'
    } catch {
      return '重置失败'
    }
  },

  loadMonth: async () => {
    const { curMonth } = get()
    const y = curMonth.getFullYear()
    const m = String(curMonth.getMonth() + 1).padStart(2, '0')
    try {
      const data = await api.get<Activity[]>(`/api/vault/activities/${y}-${m}`)
      const acts: Record<string, Activity[]> = {}
      if (Array.isArray(data)) {
        data.forEach(a => {
          if (!acts[a.date]) acts[a.date] = []
          acts[a.date].push(a)
        })
      }
      set({ monthActs: acts })
    } catch {
      set({ monthActs: {} })
    }
  },

  loadTags: async () => {
    try {
      const data = await api.get<Tag[]>('/api/vault/tags')
      set({ tags: Array.isArray(data) ? data : [] })
    } catch {
      set({ tags: [] })
    }
  },

  navigateMonth: (delta) => {
    const { curMonth } = get()
    const next = new Date(curMonth)
    next.setMonth(next.getMonth() + delta)
    set({ curMonth: next })
  },

  addActivity: async (data) => {
    try {
      const res = await api.post<{ ok: boolean }>('/api/vault/activities', data)
      if (res.ok) {
        await get().loadMonth()
        return true
      }
      return false
    } catch {
      return false
    }
  },

  updateActivity: async (id, data) => {
    try {
      const res = await api.patch<{ ok: boolean }>(`/api/vault/activity/${id}`, data)
      if (res.ok) {
        await get().loadMonth()
        return true
      }
      return false
    } catch {
      return false
    }
  },

  deleteActivity: async (id) => {
    try {
      await api.delete(`/api/vault/activity/${id}`)
      await get().loadMonth()
    } catch (e) {
      showToast(e instanceof Error ? e.message : '删除失败')
    }
  },

  addTag: async (name, icon, color) => {
    try {
      const res = await api.post<{ ok: boolean; tags: Tag[] }>('/api/vault/tags', { name, icon, color })
      if (res.ok) {
        set({ tags: res.tags })
        return true
      }
      return false
    } catch {
      return false
    }
  },

  logout: () => {
    api.vaultToken = null
    set({ isVerified: false, vaultToken: null, monthActs: {}, tags: [] })
  },
}))
